import { notFound } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetChatSessionDetailPageDataInputSchema = z.object({
  sessionId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the chat session detail page.
 *
 * Fetches the chat session in a single HTTP round trip on client-side
 * navigation. Throws `notFound()` (not a bare Response) when the session is
 * missing — thrown Responses can't be serialized by the server RPC layer
 * (Seroval crash → 500) and aren't rejected properly on the client, which
 * left the loader hanging on a blank screen with the progress bar stuck.
 * `notFound()` renders the route's `notFoundComponent` with a clean 404.
 */
export const getChatSessionDetailPageData = createServerFn({
  method: "GET",
})
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetChatSessionDetailPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("chatSessionDetail.pageData", async () => {
      const { getSessionWithMessages } = await import("./chat.server");
      const session = await getSessionWithMessages(
        context.user.id,
        data.sessionId,
      );

      if (!session) {
        throw notFound();
      }

      return { session } as const;
    });
  });
